import { type ReactElement, type ReactNode, forwardRef, useEffect } from 'react'
import { Size, useTranslation } from '@front/model'
import { update } from '@intercom/messenger-js-sdk'
import { Text, Box, createPolymorphicComponent, Drawer, type DrawerProps, Group } from '@mantine/core'
import { TButton } from 'app/components/buttons'

export type TDrawerProps = Omit<DrawerProps, 'closeButtonProps' | 'withCloseButton'> & {
  isChildDrawer?: boolean
  headerActions?: ReactNode
  hideCloseButton?: boolean
}

const _TDrawer = forwardRef<HTMLDivElement, TDrawerProps>(
  (
    { isChildDrawer, position = 'right', children, headerActions, hideCloseButton = false, ...props }: TDrawerProps,
    ref
  ): ReactElement => {
    const { t } = useTranslation()

    useEffect(() => {
      update({
        hide_default_launcher: props.opened,
      })

      return () => {
        update({
          hide_default_launcher: false,
        })
      }
    }, [props.opened])
    return (
      <Drawer
        {...props}
        position={position}
        ref={ref}
        withCloseButton={false}
        styles={{
          header: {
            paddingLeft: Size.space['5xl'],
            paddingRight: Size.space['5xl'],
            paddingBottom: Size.space['4xl'],
            paddingTop: Size.space['4xl'],
            minHeight: Size.space['4xl'] * 2 + 36,
            borderBottom: '1px solid var(--mantine-color-default-border)',
          },
          title: {
            padding: 0,
            display: 'flex',
            width: '100%',
          },
          close: {
            display: 'none',
          },
          body: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
            position: 'relative',
          },
          content: {
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        title={
          <Group align='center' justify='space-between' w='100%'>
            <Group gap={Size.space['2xl']}>
              {!hideCloseButton && (
                <TButton
                  buttonType='icon-full'
                  icon={isChildDrawer ? 'chevron_left' : 'close'}
                  variant='gray'
                  onClick={props.onClose}
                  aria-label={isChildDrawer ? t('Go back') : t('Close')}
                />
              )}
              <Text fw={700} size='lg'>
                {props.title}
              </Text>
            </Group>
            {headerActions && <Group gap={Size.space['2xl']}>{headerActions}</Group>}
          </Group>
        }
      >
        <Box className='flex flex-1 flex-col justify-between' px={Size.space['5xl']} py={Size.space['4xl']}>
          {children}
        </Box>
      </Drawer>
    )
  }
)

_TDrawer.displayName = 'TDrawer'
export const TDrawer = createPolymorphicComponent<'div', TDrawerProps>(_TDrawer)
